/**
 * Shrinking images in the browser, before they are ever uploaded.
 *
 * The server has no image library — the deliberate choice made for punch photos
 * was that the client resizes and the server only guards a byte cap. This keeps
 * that split: everything here runs on the phone that took the picture.
 *
 * The pure arithmetic is separated from the canvas work so it can be tested
 * without a browser.
 */

export interface Dimensions {
  width: number;
  height: number;
}

/**
 * Fits an image inside the target box, keeping its aspect ratio.
 *
 * Never enlarges: a 640 px frame from a cheap webcam stays 640 px rather than
 * being stretched into a bigger file with no more detail in it.
 */
export function scaleToFit(source: Dimensions, longEdge: number): Dimensions {
  const { width, height } = source;

  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { width: 0, height: 0 };
  }

  const longest = Math.max(width, height);
  if (longest <= longEdge) return { width: Math.round(width), height: Math.round(height) };

  const ratio = longEdge / longest;
  return {
    // Rounded up, so a 1079.6 never becomes 1079 and leaves a transparent seam.
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

/**
 * Long edge for an attachment.
 *
 * Higher than a punch photo's 1080, and for a different job: a punch photo has
 * to show that a person was there, while these have to stay *readable* — a
 * GCash reference number, an OR number, the total on a fuel receipt. 1600 px
 * keeps small print legible when zoomed and still lands well under the cap.
 */
export const ATTACHMENT_LONG_EDGE = 1600;

/** WebP at this quality holds text edges without ringing on receipt print. */
export const ATTACHMENT_QUALITY = 0.82;

/**
 * How many files one trip to the picker may attach.
 *
 * A purchase is a handful of receipts, never thirty. The cap exists because the
 * gallery on an Android phone offers "select all", and one mis-tap would
 * otherwise upload a camera roll onto a fuel request.
 */
export const MAX_UPLOAD_BATCH = 10;

/**
 * Whether the re-encoded version is worth sending instead of the original.
 *
 * A file that is already small re-encodes to roughly its own size, and swapping
 * it then costs a generation of quality for nothing. The 5% margin means only a
 * real saving displaces the original.
 */
export function worthReplacing(originalBytes: number, encodedBytes: number): boolean {
  if (!Number.isFinite(encodedBytes) || encodedBytes <= 0) return false;
  return encodedBytes < originalBytes * 0.95;
}

/** Swaps the extension so the name matches what is actually inside the file. */
export function renameForType(name: string, mime: string): string {
  const extension = mime === 'image/webp' ? 'webp' : mime === 'image/png' ? 'png' : 'jpg';
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  return `${base || 'attachment'}.${extension}`;
}

/**
 * Encodes the canvas, resolving null rather than throwing.
 *
 * A browser that cannot produce the asked-for type does not fail — it quietly
 * returns PNG instead. Comparing the blob's own type is what catches that, and
 * is why the WebP path can fall back rather than silently uploading a PNG that
 * is larger than the photo we started with.
 */
function encode(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob && blob.type === type ? blob : null), type, quality);
  });
}

/**
 * Downscales and re-encodes an image chosen from a phone.
 *
 * Returns the original file untouched whenever it cannot do better — an
 * unsupported type, a browser without `createImageBitmap`, a decode failure, or
 * a saving too small to be worth a re-encode. Never throws: a failure here must
 * fall back to uploading what the user picked, not block them from attaching
 * the receipt they are standing in a hardware store holding.
 */
export async function compressImageFile(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  if (typeof createImageBitmap !== 'function') return file;

  let bitmap: ImageBitmap;
  try {
    // `from-image` applies the EXIF rotation. Without it a receipt photographed
    // in portrait is drawn on its side, because the canvas ignores the tag that
    // the phone's gallery quietly honours.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }

  try {
    const size = scaleToFit({ width: bitmap.width, height: bitmap.height }, ATTACHMENT_LONG_EDGE);
    if (size.width === 0 || size.height === 0) return file;

    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;

    const context = canvas.getContext('2d');
    if (!context) return file;

    // Screenshots arrive as PNG with transparency. JPEG has no alpha, so an
    // unfilled canvas renders those areas black; white is what the receipt or
    // the banking app actually looked like.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size.width, size.height);
    context.drawImage(bitmap, 0, 0, size.width, size.height);

    const encoded =
      (await encode(canvas, 'image/webp', ATTACHMENT_QUALITY)) ??
      (await encode(canvas, 'image/jpeg', ATTACHMENT_QUALITY));

    if (!encoded || !worthReplacing(file.size, encoded.size)) return file;

    return new File([encoded], renameForType(file.name, encoded.type), {
      type: encoded.type,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  } finally {
    bitmap.close();
  }
}
